import { Helmet } from 'react-helmet';
import SectionCTA from '../components/SectionCTA';
import VerticalSteps from '../components/VerticalSteps';
import Button from '../components/ui/Button';
import { CourseIcon, DataIcon, DiscoveryIcon, LaunchIcon, PlanIcon } from '../components/icons';

const STEPS = [
  {
    icon: DiscoveryIcon,
    title: 'Discovery Meeting',
    body:
      'A guided conversation to understand your goals, values, timeline, and comfort with investment risk.',
    bullets: [
      'Clarify what success looks like and by when',
      'Identify constraints: cash needs, taxes, concentration',
      'Agree on scope and decision process'
    ]
  },
  {
    icon: DataIcon,
    title: 'Deep Dive & Data',
    body:
      'We complete a comprehensive analytical review of where you are today, including current and historical data.',
    bullets: [
      'Secure document exchange and quick-turn Q&A',
      'Tax projection and account mapping',
      'Risk, fee, and allocation diagnostics'
    ]
  },
  {
    icon: PlanIcon,
    title: 'Plan & Investments',
    body:
      'We share findings and co-create a plan that gets you on track — structured to your goals and comfort level with investments.',
    bullets: [
      'Written plan with priorities, sequence, and rules',
      'Investment Policy Statement tailored to objectives',
      'Tax-aware transition plan'
    ]
  },
  {
    icon: LaunchIcon,
    title: 'Launch & Monitor',
    body: 'After approval, we implement the plan and set a monitoring cadence to keep it on course.',
    bullets: [
      'Account opening, transfers, and automation',
      'Ongoing rebalancing and progress reviews',
      'Plain-English reports you’ll actually read'
    ]
  },
  {
    icon: CourseIcon,
    title: 'Course Corrections',
    body: 'Life changes — your plan should adapt. We review outcomes and adjust as needed.',
    bullets: [
      'Proactive outreach around markets and tax windows',
      'Annual planning session; ad-hoc check-ins anytime',
      'Updates when goals, income, or family change'
    ]
  }
];

export default function ProcessPage() {
  return (
    <main className="section-container py-10 md:py-16">
      <Helmet>
        <title>Process | Carter Asset Management</title>
      </Helmet>
      <header className="mb-8 md:mb-10">
        <p className="text-xs font-semibold uppercase tracking-wide text-sky-700/70">Our Charted Course</p>
        <h1 className="mt-2 text-2xl md:text-4xl font-semibold tracking-tight text-cam-blue">
          A disciplined framework from first conversation to ongoing guidance.
        </h1>
        <p className="mt-2 text-slate-600 max-w-3xl">
          Each stage is intentional, collaborative, and repeatable — designed to keep you oriented toward secured freedom with clear next steps.
        </p>
      </header>

      <div className="mb-8 md:mb-10 grid grid-cols-1 gap-6 md:grid-cols-2">
        <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200/90">
          <h2 className="text-lg font-semibold text-slate-900 md:text-xl">Working with an Advisor for the First Time</h2>
          <ul className="mt-3 list-disc space-y-2 pl-5 text-slate-700">
            <li>
              <strong>Clarity beats guesswork:</strong> we turn goals into a written plan with simple rules to follow.
            </li>
            <li>
              <strong>Accountability:</strong> a cadence that keeps priorities moving when life gets busy.
            </li>
            <li>
              <strong>Tax awareness:</strong> structure and timing that can meaningfully improve after-tax outcomes.
            </li>
            <li>
              <strong>Risk fit:</strong> portfolios that match your comfort level so you can stay the course.
            </li>
            <li>
              <strong>Less complexity:</strong> one team coordinating accounts, documents, and next steps.
            </li>
          </ul>
          <div className="mt-5">
            <Button href="/contact" variant="primary" className="w-full sm:w-auto">Get Started Today</Button>
          </div>
        </div>
        <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200/90">
          <h2 className="text-lg font-semibold text-slate-900 md:text-xl">Already Have an Advisor? Get a Second Opinion.</h2>
          <ul className="mt-3 list-disc space-y-2 pl-5 text-slate-700">
            <li>Objective review of fees, taxes, risk, and fit with your goals and values.</li>
            <li>Side-by-side comparison of your current portfolio vs. our proposed approach.</li>
            <li>Tax-aware transition plan to avoid unnecessary realizations and friction.</li>
            <li>Proactive outreach and plain-English reporting you can rely on.</li>
            <li>If staying put makes more sense, we’ll say so — and give you a checklist.</li>
          </ul>
          <div className="mt-5">
            <Button href="/contact" variant="secondary" className="w-full sm:w-auto">Request a Second Opinion</Button>
          </div>
        </div>
      </div>

      <section className="mb-10 md:mb-12">
        <VerticalSteps steps={STEPS} />
      </section>

      <SectionCTA
        className="my-8 md:my-10"
        title="Ready to begin the discovery conversation?"
        eyebrow="Take the first step"
        buttonText="Schedule a Call"
      />
    </main>
  );
}
